import { useState, useMemo } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  /**
   * Short status pinned to the right of the row (e.g. "in stock 60"). Unlike
   * `description` it never truncates, so the stock count stays visible even
   * when the code / interchange line is long.
   */
  badge?: string;
  /** Muted (grey) badge tone instead of the default in-stock green. */
  badgeMuted?: boolean;
  /**
   * When true, the option renders greyed-out and cannot be selected (e.g. an
   * out-of-stock item in a stock-issue picker). The `description` still shows,
   * so callers can explain why it is blocked.
   */
  disabled?: boolean;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /**
   * When true, a selected value can be cleared via an inline X button and
   * via a "— None —" entry at the top of the dropdown. Defaults to false to
   * keep required link fields tight.
   */
  clearable?: boolean;
  /** Label for the clear entry shown in the dropdown. */
  clearLabel?: string;
}

/**
 * Link-field combobox: searchable single-select.
 * Built on Radix Popover + cmdk for keyboard nav + fuzzy filter.
 */
export function Combobox({
  options, value, onChange, placeholder = 'Select…', emptyText = 'No options',
  searchPlaceholder = 'Search…', disabled, className, id,
  clearable, clearLabel = '— None —',
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const showClear = clearable && !!selected && !disabled;

  return (
    <RPopover.Root open={open} onOpenChange={setOpen}>
      <RPopover.Trigger
        id={id}
        disabled={disabled}
        className={cn(
          'input-base flex items-center justify-between gap-2 cursor-default text-left',
          !selected && 'text-text-tertiary',
          className,
        )}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <span className="flex items-center gap-1 shrink-0">
          {showClear && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear selection"
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(null); } }}
              className="text-text-tertiary hover:text-text-primary"
            >
              <X className="size-3.5" />
            </span>
          )}
          <ChevronsUpDown className="size-3.5 text-text-tertiary" />
        </span>
      </RPopover.Trigger>

      <RPopover.Portal>
        <RPopover.Content
          align="start"
          sideOffset={4}
          className="z-50 surface-card shadow-overlay !p-0 overflow-hidden w-[var(--radix-popover-trigger-width)] min-w-[240px] animate-scale-in"
        >
          <Command>
            <div className="flex items-center gap-2 px-3 border-b border-border">
              <Search className="size-3.5 text-text-tertiary shrink-0" />
              <Command.Input
                autoFocus
                placeholder={searchPlaceholder}
                className="!border-0 !py-2 !px-0 text-body"
              />
            </div>
            <Command.List className="max-h-[280px] overflow-y-auto p-1">
              <Command.Empty>{emptyText}</Command.Empty>
              {clearable && (
                <Command.Item
                  key="__clear__"
                  value={clearLabel}
                  onSelect={() => { onChange(null); setOpen(false); }}
                >
                  <div className="flex-1 min-w-0 text-body text-text-tertiary italic truncate">{clearLabel}</div>
                </Command.Item>
              )}
              {options.map((opt) => (
                <Command.Item
                  key={opt.value}
                  value={`${opt.label} ${opt.description ?? ''}`}
                  disabled={opt.disabled}
                  onSelect={() => { if (opt.disabled) return; onChange(opt.value); setOpen(false); }}
                  className={cn(opt.disabled && 'opacity-45 cursor-not-allowed data-[selected=true]:bg-transparent')}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-body text-text-primary truncate">{opt.label}</div>
                    {opt.description && (
                      <div className="text-caption text-text-tertiary truncate">{opt.description}</div>
                    )}
                  </div>
                  {opt.badge && (
                    <span className={cn(
                      'text-caption shrink-0 whitespace-nowrap tabular-nums',
                      opt.badgeMuted ? 'text-text-tertiary' : 'text-success',
                    )}>
                      {opt.badge}
                    </span>
                  )}
                  {opt.value === value && <Check className="size-4 text-accent shrink-0" />}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}
